import { inject, Injectable } from '@angular/core';
import { AccountCheckIn, CheckinScheduleInstance, IProgressReportAttachment, ProgressReport, User, checkinSchedule } from '../models';
import { HelperService } from './helper.service';
import { DatabaseService } from './database.service';
import { StorageService } from './storage.service';
import { Observable } from 'rxjs';
import { limitToLast, orderByChild, equalTo} from '@angular/fire/database';
import { Firestore, query, collection, where, orderBy, collectionData, doc, updateDoc } from '@angular/fire/firestore';
import { LocalAttachment } from './camera.service';

@Injectable({
  providedIn: 'root',
})
export class CheckinService {

  private firestore = inject(Firestore);  // Inject Firestore instance

  private checkinFields = {
    weight: {
      order: 1,
      name: "Weight",
      value: "weight",
      unit: "lbs",
      units: {
        imperial: "lbs",
        metric: "kg"
      }
    },
    bodyfat:   {
      order: 2,
      name: "Bodyfat",
      value: "bodyfat",
      unit: "%",
      units: {
        imperial: "%",
        metric: "%"
      }
    },
   /*lbm: {
      order: 3,
      name: "LBM",
      value: "lbm",
      unit: "lbs",
      units: {
        imperial: "lbs",
        metric: "kg"
      }
    },*/
    fat: {
      order: 4,
      name: "Fat Mass",
      value: "fat",
      unit: "lbs",
      units: {
        imperial: "lbs",
        metric: "kg"
      }
    },
    bicepRight: {
      order: 5,
      name: "Right Bicep",
      value: "bicepRight",
      unit: "in",
      units: {
        imperial: "in",
        metric: "cm"
      }
    },
    bicepLeft: {
      order: 6,
      name: "Left Bicep",
      value: "bicepLeft",
      unit: "in",
      units: {
        imperial: "in",
        metric: "cm"
      }
    },
    shoulders: {
      order: 7,
      name: "Shoulders",
      value: "shoulders",
      unit: "in",
      units: {
        imperial: "in",
        metric: "cm"
      }
    },
    chest: {
      order: 8,
      name: "Chest",
      value: "chest",
      unit: "in",
      units: {
        imperial: "in",
        metric: "cm"
      }
    },
    waist: {
      order: 9,
      name: "Waist",
      value: "waist",
      unit: "in",
      units: {
        imperial: "in",
        metric: "cm"
      }
    },
    glutes: {
      order: 10,
      name: "Glutes",
      value: "glutes",
      unit: "in",
      units: {
        imperial: "in",
        metric: "cm"
      }
    },
    quadRight: {
      order: 11,
      name: "Right Thigh",
      value: "quadRight",
      unit: "in",
      units: {
        imperial: "in",
        metric: "cm"
      }
    },
    quadLeft: {
      order: 12,
      name: "Left Thigh",
      value: "quadLeft",
      unit: "in",
      units: {
        imperial: "in",
        metric: "cm"
      }
    },
    calfRight: {
      order: 13,
      name: "Right Calf",
      value: "calfRight",
      unit: "in",
      units: {
        imperial: "in",
        metric: "cm"
      }
    },
    calfLeft: {
      order: 14,
      name: "Left Calf",
      value: "calfLeft",
      unit: "in",
      units: {
        imperial: "in",
        metric: "cm"
      }
    },
  } 

  public accountCheckIns: AccountCheckIn[];

  constructor(
    private dbService: DatabaseService,
    private helperService: HelperService,
    private storageService: StorageService,
  ) {}

  addCheckIn(progressReport: Partial<ProgressReport>, userId: string, createdBy: string) {
    progressReport.createdBy = createdBy;
    progressReport.creationDate = new Date().toISOString();
    return this.dbService.pushByRef(`/progressReports/${userId}`, progressReport);
  }

  updateProgressReport(reportId: string, userId: string, progressReportDetail, modifiedBy: string) {
    const toUpdate = {
      ...progressReportDetail,
      lastModifiedDate: new Date().toISOString(),
      lastModifiedBy: modifiedBy,
    };
    delete toUpdate.id;
    return this.dbService.updateRef(`/progressReports/${userId}/${reportId}`, toUpdate);
  }

  async getProgressReportsAsPromise(userId: string): Promise<ProgressReport[]> {
    const snap = await this.dbService.getSnapshot(`progressReports/${userId}`);
    return snap? this.helperService.transformFirebaseObjectToArray(snap).map((report) => {return new ProgressReport(report)}) : null;
  }

  async getCheckinAsPromise(userId: string = null, reportId: string): Promise<ProgressReport> {
    const snap = await this.dbService.getSnapshot(`/progressReports/${userId}/${reportId}`);
    return snap? new ProgressReport({id: reportId, ...snap}) : null;
  }

  getUserProgressReportList(userId: string): Observable<ProgressReport[]> {
    return this.dbService.getListByRefTest(`progressReports/${userId}`)
  }

async addProgressReportAttachment(
  userId: string,
  reportId: string,
  attachment: LocalAttachment,
  notes?: string
) {

  const storagePath = `users/${userId}/progressReports/${reportId}/${attachment.name}`;
  let attachmentRef: string;

  if(attachment.file) {
    attachmentRef = await this.storageService.fbPutFile(storagePath, attachment.file);
  }

  if(attachment.nativePath) {
    attachmentRef = await this.storageService.fpPutNativeFile(storagePath, attachment.nativePath);
  }

  if(!attachmentRef) {
    throw new Error('Unable to upload attachment.');
  }

  const data = {
    fileName: attachment.name,
    type: attachment.type,
    url: attachmentRef,
    notes: notes || null,
  };

  const ref = await this.dbService.pushByRef(`/progressReports/${userId}/${reportId}/attachments`, data);
  return {
    ...data,
    id: ref.key
  };
}

  async removeProgressReportAttachment(userId: string, reportId: string, fileName: string) {
    return this.dbService.removeRef(`/progressReports/${userId}/${reportId}/attachments/${fileName}`);
  }

  async updateProgressReportAttachment(userId: string, reportId: string, attachmentId: string, data: Partial<IProgressReportAttachment> = {}, attachment?: Blob) {
    if(attachment) {
      const downloadURL = await this.storageService.fbPutFile(`users/${userId}/progressReports/${reportId}/${data.fileName}`, attachment);
      const fileData = {
        type: attachment.type,
        url: downloadURL,
      };
      Object.assign(data, fileData);
    }
    await this.dbService.updateRef(`/progressReports/${userId}/${reportId}/attachments/${attachmentId}`, data);
    return {
      ...data
    }
  }

  async getMostRecentProgressReport(userId) {
    const snap = await this.dbService.getSnapshot(`progressReports/${userId}`, orderByChild("creationDate"), limitToLast(1));
    if(snap) {
      const prReports = this.helperService.transformFirebaseObjectToArray(snap);
      return new ProgressReport(prReports[0]);
    } else {
      return null
    }
  }

  deleteProgressReport(userId: string, reportId: string) {
    return this.dbService.removeRef(`/progressReports/${userId}/${reportId}`);
  }

  getCheckinFields() {
    return this.helperService.copyObject(this.checkinFields);
  }

  getCheckinFieldsAsArray() {
    const fields = this.getCheckinFields();
    const keys = Object.keys(fields);
    const toReturn = [];
    for (const key of keys) {
      toReturn.push({
        ...fields[key],
        id: key
      })
    }
    return toReturn.sort(function(a, b){ 
      return a.order - b.order 
    })
  }

  getCheckinField(fieldValue: string) {
    return this.checkinFields[fieldValue]
  }

  reportToMetric(report: Partial<ProgressReport>): Partial<ProgressReport> {
    const weightFactor = 0.454;
    const lengthFactor = 2.54;
    report.weight = report.weight? Math.round(report.weight * weightFactor * 10) / 10 : null;
    report.lbm = report.lbm? Math.round(report.lbm * weightFactor * 10) / 10 : null;
    report.fat = report.fat? Math.round(report.fat * weightFactor * 10) / 10: null;
    report.bicepRight = report.bicepRight? Math.round(report.bicepRight * lengthFactor * 10) / 10 : null;
    report.bicepLeft = report.bicepLeft? Math.round(report.bicepLeft * lengthFactor * 10) / 10 : null;
    report.shoulders = report.shoulders? Math.round(report.shoulders * lengthFactor * 10) / 10 : null;
    report.chest = report.chest? Math.round(report.chest * lengthFactor * 10) / 10 : null;
    report.waist = report.waist? Math.round(report.waist * lengthFactor * 10) / 10 : null;
    report.glutes = report.glutes? Math.round(report.glutes * lengthFactor * 10) / 10 : null;
    report.quadLeft = report.quadLeft? Math.round(report.quadLeft * lengthFactor * 10) / 10 : null;
    report.quadRight = report.quadRight? Math.round(report.quadRight * lengthFactor * 10) / 10 : null;
    report.calfLeft = report.calfLeft? Math.round(report.calfLeft * lengthFactor * 10) / 10 : null;
    report.calfRight= report.calfRight? Math.round(report.calfRight * lengthFactor * 10) / 10 : null;
    return report
  }

  reportToImperial(report: Partial<ProgressReport>): Partial<ProgressReport> {
    const weightFactor = 0.454;
    const lengthFactor = 2.54;
    report.weight = report.weight? Math.round(report.weight / weightFactor * 10) / 10 : null;
    report.lbm = report.lbm? Math.round(report.lbm / weightFactor * 10) / 10 : null;
    report.fat = report.fat? Math.round(report.fat / weightFactor * 10) / 10 : null;
    report.bicepRight = report.bicepRight? Math.round(report.bicepRight / lengthFactor * 10) / 10 : null;
    report.bicepLeft = report.bicepLeft? Math.round(report.bicepLeft / lengthFactor * 10) / 10 : null;
    report.shoulders = report.shoulders? Math.round(report.shoulders / lengthFactor * 10) / 10 : null;
    report.chest = report.chest? Math.round(report.chest / lengthFactor * 10) / 10 : null;
    report.waist = report.waist? Math.round(report.waist / lengthFactor * 10) / 10 : null;
    report.glutes = report.glutes? Math.round(report.glutes / lengthFactor * 10) / 10 : null;
    report.quadLeft = report.quadLeft? Math.round(report.quadLeft / lengthFactor * 10) / 10 : null;
    report.quadRight = report.quadRight? Math.round(report.quadRight / lengthFactor * 10) / 10 : null;
    report.calfLeft = report.calfLeft? Math.round(report.calfLeft / lengthFactor * 10) / 10 : null;
    report.calfRight= report.calfRight? Math.round(report.calfRight / lengthFactor * 10) / 10 : null;
    return report
  }

  // Schedule
  async getCheckinScheduleAsPromise(orgId: string, scheduleId): Promise<checkinSchedule> {
    const snap = await this.dbService.getSnapshot(`/checkinSchedules/${orgId}/definitions/${scheduleId}`);
    return snap? new checkinSchedule({
      id: scheduleId,
      ...snap
    }) : null
  } 

  async getCheckinSchedules(orgId: string): Promise<checkinSchedule[]> {
    const snap = await this.dbService.getSnapshot(`/checkinSchedules/${orgId}/definitions`);
    return snap? this.helperService.transformFirebaseObjectToArray(snap) : [];
  } 

  getCheckinSchedulesListByUser(orgId: string, userId: string) {
    return this.dbService.getListByRefTest(`checkinSchedules/${orgId}/definitions`, orderByChild("user"), equalTo(userId));
  }

  async getCheckinSchedulesByUser(orgId: string, userId: string) {
    const snap = await this.dbService.getSnapshot(`checkinSchedules/${orgId}/definitions`, orderByChild("user"), equalTo(userId));
    if(snap) {
      return this.helperService.transformFirebaseObjectToArray(snap)
      .map((schedule) => {
        return new checkinSchedule(schedule)
      });
    } else {
      return null
    }
  }

  addCheckinSchedule(orgId: string, schedule: Partial<checkinSchedule>) {
    return this.dbService.pushByRef(`/checkinSchedules/${orgId}/definitions`, {
      ...schedule,
      creationDate: new Date().toISOString()
    });
  }

  updateCheckinSchedule(orgId: string, scheduleId: string, schedule: Partial<checkinSchedule>) {
    return this.dbService.updateRef(`/checkinSchedules/${orgId}/definitions/${scheduleId}`, {
      ...schedule,
      creationDate: new Date().toISOString()
    });
  }

  deleteCheckinSchedule(userId: string, scheduleId: string) {
    return this.dbService.removeRef(`/checkinSchedules/${userId}/definitions/${scheduleId}`);
  }

  async getCheckinScheduleInstancesByDate(orgId: string, date: string): Promise<CheckinScheduleInstance[]> {
    const snap = await this.dbService.getSnapshot(`/checkinSchedules/${orgId}/instances`, orderByChild("date"), equalTo(date));
    return snap? this.helperService.transformFirebaseObjectToArray(snap) : [];
  }

  async getCheckinScheduleInstancesByStatus(orgId: string, status: "scheduled" | "complete"): Promise<CheckinScheduleInstance[]> {
    const snap = await this.dbService.getSnapshot(`/checkinSchedules/${orgId}/instances`, orderByChild("status"), equalTo(status));
    return snap? this.helperService.transformFirebaseObjectToArray(snap) : [];
  }

  getCheckinScheduleInstancesListByStatus(orgId: string, status: "scheduled" | "complete") {
    return this.dbService.getListByRefTest(`/checkinSchedules/${orgId}/instances`, orderByChild("status"), equalTo(status));
  }

  // Account Check Ins

  getAccountCheckIn(orgId, checkinId): Promise<AccountCheckIn> {
    return this.dbService.getSnapshot(`organizationProgressReports/${orgId}/${checkinId}`);
  }

    /**
   * Get progress reports for a specific organization in real-time.
   * @param orgId The organization ID
   * @returns Observable of progress reports
   */
  /*getAccountCheckIns(orgId: string): Observable<any[]> {
    const reportsRef = collection(this.firestore, `organizations/${orgId}/progressReports`);
    const q =
     query(
      reportsRef,
      where('status', 'in', ['new', 'review']),
      orderBy('published', 'desc')
    );

    return collectionData(q, { idField: 'id' });
  }*/

  getAccountCheckinsByStatus(orgId: string, status: "new" | "review" | "pending") {
    return this.dbService.getListByRefTest(`/organizationProgressReports/${orgId}`, orderByChild("status"), equalTo(status));
  }

  /**
   * Update a specific progress report's fields.
   * @param orgId The organization ID
   * @param progressReportId The specific report's ID
   * @param updatedData The fields to update (status, userId, etc.)
   * @returns Promise for the update operation
   */
  updateAccountCheckIn(orgId: string, progressReportId: string, updatedData: Partial<AccountCheckIn>): Promise<void> {
    /*const reportRef = doc(this.firestore, `organizations/${orgId}/progressReports/${progressReportId}`);
    
    // Update the report with the provided fields
    return updateDoc(reportRef, updatedData);*/
    return this.dbService.updateRef(`/organizationProgressReports/${orgId}/${progressReportId}`, updatedData);
  }

  removeProgressReport(reportId: string, userId: string) {
    return this.dbService.removeRef(`/progressReports/${userId}/${reportId}`);
  }

  getProgressReportCommentsList(reportId: String, userId: string) {
    return this.dbService.getListByRefTest(`/progressReports/${userId}/${reportId}/comments`);
  }

  AddProgressReportComment(userId, reportId, comment) {
    return this.dbService.pushByRef(`/progressReports/${userId}/${reportId}/comments`, comment);
  }  

  async addProgressReportCommentAttachment(
    userId: string,
    reportId: string,
    createUser: User,
    attachment: LocalAttachment,
    organization?: string,
    uploadOptions?: { onProgress?: (progress: { percent: number }) => void }
  ) {
    const storagePath = `users/${userId}/progressReports/${reportId}/comments/${attachment.name}`;
    let attachmentRef = null;

    if(attachment.file) {
      attachmentRef = await this.storageService.fbPutFile(storagePath, attachment.file, {
        resumable: true,
        onProgress: uploadOptions?.onProgress
          ? (p) => uploadOptions.onProgress({ percent: p.percent })
          : undefined,
      });
    }

    if(attachment.nativePath) {
      attachmentRef = await this.storageService.fpPutNativeFile(storagePath, attachment.nativePath);
    }

    if(!attachmentRef) {
      throw new Error('No attachment reference found');
    }

    return this.AddProgressReportComment(userId, reportId, {
      comment: null,
      creationDate: new Date().toISOString(),
      createdBy: createUser.uid,
      createdByName: createUser.getFullName(),
      organization: organization || null,
      attachments: [{
        url: attachmentRef,
        fileName: attachment.name,
        type: attachment.type,
      }]
    });
  }  

  updateProgressReportComment(userId, reportId, commentId, comment) {
    return this.dbService.updateRef(`/progressReports/${userId}/${reportId}/comments/${commentId}`, comment)
  }  

  deleteProgressReportComment(userId, reportId, commentId) {
    return this.dbService.removeRef(`/progressReports/${userId}/${reportId}/comments/${commentId}`);
  } 
}